<template>
	<modal-side-generic
	:applyErrorState="applyErrorState"
	:headerColor="headerColor"
	:title="selectedPaymentsRequest.merchant.friendlyName"
	:subTitle="estimatedToMaxAmount"
	:subTitleIcon="headerTitleIcon"
	:icon="headerBigIcon"
	:buttonsTopData="buttonsTopDataHandler"
	:showFooter="showFooterHandler"
	:buttonsFooterData="buttonsFooterData"
	@hideMe="emit('hideMe')">
		<div
		v-for="(lineGroup, index) in paymentsRequestDetail.filter(obj => !obj.hideField)"
		class="group-wrapper"
		:key="`lineGroup${lineGroup.index}`"
		:class="{ 'with-table': lineGroup.hasTable, 'mobile': getMobileAppState }"
		:style="{ 'padding': lineGroup.hasTable && !getMobileAppState ? '0px': null }">
			<!-- Divider -->
			<divider-generic
			:icon="lineGroup.icon"
			:title="lineGroup.label"
			:applyBorder="index !== 0"/>
			<!-- Blocks -->
			<div v-for="(line, index) in lineGroup.lines" :key="`lineGroupLine${index}`" class="line-content">
				<!-- Show card with Info -->
				<infobox-generic
				v-if="line.card && getPaymentsDetail.status === 'ACCEPTED'"
				:content="line.card"/>

				<!-- Text Block for each line -->
				<text-block-generic
                v-if="!line.card"
				applyEllipsis
				:params="line"
				:icon="line.icon"
				:colorIconPreset="line.colorIconPreset"
				:mask="line.mask"/>

				<!-- Show table case shoppingCart exists -->
				<table-generic
                v-if="line.shoppingCart && !getMobileAppState"
				noBoxStyles
				rowHeightClass="small"
				:headerData="shoppingCartHeaderData"
				:tableStyles="shoppingCartTableStyles"
				:tableData="shoppingCartTableData"/>

				<!-- Show table case shoppingCart exists: Mobile Version -->
				<shopping-cart
                v-if="line.shoppingCart && getMobileAppState"
                :items="line.shoppingCart.items"
                :currency="getPaymentsDetail.currency"/>
			</div>
		</div>
        <!-- Loader Generic -->
        <loaders-generic v-if="showLoader"/>
	</modal-side-generic>
    <!-- FeedbackToast -->
	<toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import ModalSideGeneric from '@/components/generic/modals/side/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import ShoppingCart from '@/components/features/payments/requests/ShoppingCart'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue & Helpers */
import { ref, computed } from 'vue'
import { amountFormat } from '@/assets/scripts/amounts'
import { getDateWithTime } from '@/assets/scripts/dateBuilders'
import { ACCOUNT_NUMBER } from '@/assets/constants/masks'
import { iconMapper, iconColorMapper, colorStatusMapperModal } from '@/assets/scripts/mappers/paymentsRequests'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { createDownloadMobilePaymentsReceiptPdf } from '@/network/api/mobilePayments.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe', 'redirectToPaymentsFeature', 'cancelPaymentsRequest'])

/* mapActions & mapGetters */
const { getMobilePaymentDetail } = mapActions(moduleMapper.PAYMENTS_REQUESTS_BAI_PAGA)
const { getPaymentsDetail } = mapGetters(moduleMapper.PAYMENTS_REQUESTS_BAI_PAGA)
const { getMobileAppState } = mapGetters()

const props = defineProps({
	selectedPaymentsRequest: Array
})

/* Data */
const buttonsTopData = ref({})
const buttonsFooterData = ref([])
const showLoader = ref(false)
const applyErrorState = ref(false)

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestPaymentDetail = async () => {
    showLoader.value = true
	applyErrorState.value = false

	try {
		await getMobilePaymentDetail(props.selectedPaymentsRequest.id)
        showLoader.value = false
	} catch (error) {
		applyErrorState.value = true
		errorHandler(error.response.data, requestPaymentDetail)
	}
}

/* Creating estimatedToMaxAmount value */
let estimatedToMaxAmount

if (props.selectedPaymentsRequest.amount || props.selectedPaymentsRequest.amount === 0) {
	estimatedToMaxAmount = amountFormat(Math.abs(props.selectedPaymentsRequest.amount), props.selectedPaymentsRequest.currency)
} else {
	const initialAmount = amountFormat(Math.abs(props.selectedPaymentsRequest.estimatedAmount), props.selectedPaymentsRequest.currency)
	const endAmount = amountFormat(Math.abs(props.selectedPaymentsRequest.maxAmount))

	estimatedToMaxAmount = initialAmount + t('payments.requests.baiPaga.tableData.to') + endAmount
}

/*************************************************
*                                                *
*                Side Modal Info                 *
*                                                *
*************************************************/
const headerColor = computed(() => {
	return colorStatusMapperModal(props.selectedPaymentsRequest.status)
})

const headerBigIcon = computed(() => {
	return iconMapper(props.selectedPaymentsRequest.status)
})

const headerTitleIcon = computed(() => {
	if (props.selectedPaymentsRequest.amount === 0) return null
	return 'arrow-down-full'
})

buttonsTopData.value = {
	downloadUrl: createDownloadMobilePaymentsReceiptPdf(props.selectedPaymentsRequest.id),
	shareLinkPayload: 'generateShareLink',
	emailAction: 'sendMobilePaymentEmailNotification',
	smsAction: 'sendMobilePaymentSmsNotification',
	sendPayload: { operationId: props.selectedPaymentsRequest.id },
	operationId: props.selectedPaymentsRequest.operationId
}

const buttonsTopDataHandler = computed(() => {
	return props.selectedPaymentsRequest.status === 'SUCCESS' ? buttonsTopData.value : null
})

buttonsFooterData.value = [{
	label: t('payments.requests.baiPaga.detailModal.buttonsFooter.leftButtonText'),
	secondVariant: true,
    buttonPosition: 'left',
    buttonType: 'negative',
	callbackFunction: () => { emit('cancelPaymentsRequest') }
	}, {
	label: t('payments.requests.baiPaga.detailModal.buttonsFooter.rightButtonText'),
	secondVariant: false,
    buttonPosition: 'right',
    buttonType: 'primary',
	callbackFunction: () => { emit('redirectToPaymentsFeature', getPaymentsDetail.value) }
	}
]

const showFooterHandler = computed(() => {
	return getPaymentsDetail.value.status === 'PENDING_ACCEPTANCE' &&
		(getPaymentsDetail.value.flow === 'PAYMENT_REQUESTED_FROM_MERCHANT_CAPTIVE' || getPaymentsDetail.value.flow === 'PAYMENT_REQUESTED_FROM_MERCHANT') ? buttonsFooterData.value : null
})

/*************************************************
*                                                *
*            Payment Request Detail              *
*                                                *
*************************************************/
const paymentsRequestDetail = computed(() => {
	const amountBlock = {
		label: t('payments.requests.baiPaga.detailModal.amount'),
		icon: 'money',
		lines: [{
			label: t('payments.requests.baiPaga.detailModal.amount'),
			content: estimatedToMaxAmount
		}]
	}

	if (getPaymentsDetail.value.status === 'ACCEPTED') {
		amountBlock.lines.push({
			card: t('payments.requests.baiPaga.detailModal.amountInfo')
		})
	}

	return [{
		label: t('payments.requests.baiPaga.detailModal.merchantInfo'),
		icon: 'cart',
		lines: [{
			label: t('payments.requests.baiPaga.detailModal.merchantId'),
			content: props.selectedPaymentsRequest.merchant.id ? props.selectedPaymentsRequest.merchant.id : '-'
		}, {
			label: t('payments.requests.baiPaga.detailModal.merchant'),
			content: props.selectedPaymentsRequest.merchant.friendlyName ? props.selectedPaymentsRequest.merchant.friendlyName : '-'
		}]
	}, {
		label: t('payments.requests.baiPaga.detailModal.senderAccount'),
		icon: 'account-balance',
		hideField: !props.selectedPaymentsRequest.accountNumber,
		lines: [{
			label: t('payments.requests.baiPaga.detailModal.account'),
			content: props.selectedPaymentsRequest.accountNumber ? props.selectedPaymentsRequest.accountNumber : '-',
			mask: props.selectedPaymentsRequest.accountNumber ? ACCOUNT_NUMBER : null
		}]
	}, {
		label: t('payments.requests.baiPaga.detailModal.paymentInfo'),
		icon: 'coin-drop',
		hasTable: true,
		hideField: !props.selectedPaymentsRequest.shoppingCart,
		lines: [{
			shoppingCart: props.selectedPaymentsRequest.shoppingCart,
			label: t('payments.requests.baiPaga.detailModal.description'),
			content: t('payments.requests.baiPaga.detailModal.paymentDescription')
		}]
	},
	amountBlock,
	{
		label: t('payments.requests.baiPaga.detailModal.info'),
		icon: 'info',
		lines: [{
			label: t('payments.requests.baiPaga.detailModal.status'),
			icon: iconMapper(getPaymentsDetail.value.status),
			colorIconPreset: iconColorMapper(getPaymentsDetail.value.status),
			content: getPaymentsDetail.value.statusDescription
		}, {
			label: t('payments.requests.baiPaga.detailModal.beginDate'),
			content: getDateWithTime(getPaymentsDetail.value.creationDate),
			classes: 'capitalize'
		}, {
			label: t('payments.requests.baiPaga.detailModal.endDate'),
			/* If no lastChangeDate is provided, it's because the last change was the creation, so we will use creationDate instead */
			content: getDateWithTime(getPaymentsDetail.value.lastChangeDate || getPaymentsDetail.value.creationDate),
			classes: 'capitalize'
		}]
	}]
})

const shoppingCartHeaderData = [
	{ label: t('payments.requests.baiPaga.detailModal.article'), dataAttr: 'col1', rowCellType: 'title', showInMobile: true },
	{ label: t('payments.requests.baiPaga.detailModal.unitCost'), dataAttr: 'col2', rowCellType: 'amount', showInMobile: true },
	{ label: t('payments.requests.baiPaga.detailModal.quantity'), dataAttr: 'col3', showInMobile: true },
	{ label: t('payments.requests.baiPaga.detailModal.discount'), dataAttr: 'col4', showInMobile: true }
]

const shoppingCartTableStyles = [
	{ width: '180px', 'min-width': '180px', 'padding-left': '24px' },
	{ 'text-align': 'end', width: '102px', 'min-width': '102px' },
	{ 'text-align': 'end', width: '36px', 'min-width': '36px' },
	{ 'text-align': 'end', width: '76px', 'min-width': '76px' }
]

const shoppingCartTableData = computed(() => {
	return props.selectedPaymentsRequest.shoppingCart.items.map(item => {
		return [
			{
				type: 'title',
				value: item.description
			},
			{
				type: 'amount',
				value: { content: item.amountPerItem, currency: props.selectedPaymentsRequest.currency, noBalanceIcon: true }
			},
			{ value: item.count },
			{ value: { content: item.discount + ' %' } }
		]
	})
})

/************************************************
*												*
*                 Error Feedback                *
*												*
************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

/************************************************
*												*
*             Success/Error Handlers            *
*												*
************************************************/
const errorHandler = (response, retryFn) => {
	showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(response, retryFn)
	showFeedbackToast.value = true
}

requestPaymentDetail()
</script>

<style lang="scss" scoped>
.group-wrapper {
	.line-content {
		width: 100%;

		display: flex;
		flex-direction: column;
		gap: 24px;
	}

	&.with-table:not(.mobile) {
		.divider-generic-wrapper, .text-blocks-wrapper {
			width: calc(100% - 24px);
			margin-left: 24px;
		}
	}
}
</style>
